import { isViewingChat } from './chatPresence';
import { getPushNotificationUrl } from '../utils/navigation';
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { app } from '../firebase';
import { updateNotificationToken } from './firebaseData';

const WEBVIEWGOLD_TOKEN_KEYS = [
  'firebase_push_id',
  'firebaseplayerid',
  'firebasePlayerId',
  'firebasePlayerID',
  'firebasePushId',
  'firebasePushID',
  'webviewgold_firebase_push_id',
];

let foregroundUnsubscribe = null;
let lastInitializationKey = '';

function notificationPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

function getPublicUrl(path) {
  const base = process.env.PUBLIC_URL || '';
  return `${base}${path}`;
}

function getNotificationsUrl() {
  return getPushNotificationUrl();
}

function readWebViewGoldTokenFromRuntime() {
  if (typeof window === 'undefined') return '';
  const params = new URLSearchParams(window.location.search);
  for (const key of WEBVIEWGOLD_TOKEN_KEYS) {
    const paramToken = params.get(key);
    if (paramToken) {
      window.localStorage?.setItem('webviewgold_firebase_push_id', paramToken);
      return paramToken;
    }
    if (window[key]) {
      window.localStorage?.setItem('webviewgold_firebase_push_id', window[key]);
      return window[key];
    }
  }
  return window.localStorage?.getItem('webviewgold_firebase_push_id') || '';
}

function requestWebViewGoldFirebaseToken() {
  if (typeof document === 'undefined' || !document.body) return;
  const bridgeFrame = document.createElement('iframe');
  bridgeFrame.title = 'firebase-token-bridge';
  bridgeFrame.style.display = 'none';
  bridgeFrame.src = 'getfirebaseplayerid://';
  document.body.appendChild(bridgeFrame);
  window.setTimeout(() => {
    bridgeFrame.remove();
  }, 1500);
}

async function syncWebViewGoldFirebaseToken(userId) {
  let token = readWebViewGoldTokenFromRuntime();

  if (!token) {
    requestWebViewGoldFirebaseToken();
    for (let attempt = 0; attempt < 6; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 300));
      token = readWebViewGoldTokenFromRuntime();
      if (token) break;
    }
  }

  if (!token) return null;

  await updateNotificationToken(userId, {
    provider: 'firebase-native',
    firebaseNativeToken: token,
    firebaseToken: token,
    permission: notificationPermission(),
    appOpenUrl: getNotificationsUrl(),
  });

  return token;
}

async function ensureMessagingRegistration() {
  if (!('serviceWorker' in navigator)) return null;
  return navigator.serviceWorker.register(getPublicUrl('/firebase-messaging-sw.js'));
}

async function syncBrowserFirebaseToken(userId) {
  const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
  if (typeof Notification === 'undefined') return null;

  const supported = await isSupported();
  if (!supported) return null;

  const permission = Notification.permission === 'granted'
    ? 'granted'
    : await Notification.requestPermission();

  if (permission !== 'granted') {
    await updateNotificationToken(userId, {
      provider: 'firebase-web',
      permission,
      appOpenUrl: getNotificationsUrl(),
    });
    return null;
  }

  const serviceWorkerRegistration = await ensureMessagingRegistration();
  if (!serviceWorkerRegistration) return null;

  const messaging = getMessaging(app);
  const tokenOptions = { serviceWorkerRegistration };
  if (vapidKey) tokenOptions.vapidKey = vapidKey;
  const token = await getToken(messaging, tokenOptions);

  if (!token) return null;

  await updateNotificationToken(userId, {
    provider: 'firebase-web',
    firebaseWebToken: token,
    firebaseToken: token,
    permission,
    appOpenUrl: getNotificationsUrl(),
  });

  return token;
}

function wireForegroundNotifications() {
  if (foregroundUnsubscribe || typeof Notification === 'undefined') return;
  isSupported().then((supported) => {
    if (!supported) return;
    const messaging = getMessaging(app);
    foregroundUnsubscribe = onMessage(messaging, async (payload) => {
      if (Notification.permission !== 'granted' || !('serviceWorker' in navigator)) return;
      const data = payload.data || {};
      if (data.type === 'CHAT_MESSAGE' && isViewingChat(data.groupId)) return;
      const registration = await ensureMessagingRegistration();
      const notification = payload.notification || {};
      await registration.showNotification(notification.title || data.title || 'Convivial', {
        body: notification.body || data.body || data.message || 'Hai una nuova notifica.',
        icon: data.icon || getPublicUrl('/logo192.png'),
        badge: data.badge || getPublicUrl('/logo192.png'),
        tag: data.tag || data.notificationId || 'convivial-notification',
        data: {
          ...data,
          url: getPushNotificationUrl(data),
        },
      });
    });
  }).catch((error) => {
    console.error('Unable to initialize foreground Firebase notifications:', error);
  });
}

export async function initializePushNotifications(userId) {
  if (!userId || typeof window === 'undefined') return;
  const initializationKey = `${userId}:${window.location.search}`;
  if (lastInitializationKey === initializationKey) return;
  lastInitializationKey = initializationKey;

  wireForegroundNotifications();

  try {
    await syncWebViewGoldFirebaseToken(userId);
  } catch (error) {
    console.error('Unable to sync WebViewGold Firebase token:', error);
  }

  try {
    await syncBrowserFirebaseToken(userId);
  } catch (error) {
    console.error('Unable to sync browser Firebase token:', error);
  }
}
